import { FC, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import { IconArrowRight, Icon } from '@tabler/icons-react';
import { BinLocationCard } from 'views/warehouse/components/BinLocationCard';
import { MoveWarningData } from 'views/warehouse/MoveWarningContext/types';
import {
  requestTypeToPurpose,
  KIT_REQUEST_PURPOSES,
} from 'views/warehouse/utils';
import { StockLotRequestCard } from 'views/warehouse/MoveWarningContext/components/StockLotDisplay/StockLotRequestCard';
import {
  StockLocationPurpose,
  binPurposeIconMap,
} from 'types/clientV2/stockLocations';
import { HookFormClickableBox } from 'ui-component/HookFormComponents';
import { useTypedForm } from 'hooks/useTypedForm';
import * as Yup from 'yup';
import { useAssignStockLocationsMutation } from 'store/slices/clientV2/stockLocations';
import useSnackbar from 'hooks/useSnackbar';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { handleErr } from 'utils/functions';
import { getOrgId } from 'views/warehouse/MoveWarningContext/utils';

type UnassignedBinContentProps = {
  warningData: MoveWarningData;
  onDismiss: () => void;
};

const unassignedBinSchema = Yup.object({
  purpose: Yup.string()
    .oneOf([StockLocationPurpose.PUTTING, StockLocationPurpose.STORAGE])
    .required(),
});

type UnassignedBinFormData = Yup.InferType<typeof unassignedBinSchema>;

const unassignedBinOptions = [
  {
    id: StockLocationPurpose.PUTTING,
    label: 'Putting',
    Icon: binPurposeIconMap[StockLocationPurpose.PUTTING] as Icon,
  },
  {
    id: StockLocationPurpose.STORAGE,
    label: 'Storage',
    Icon: binPurposeIconMap[StockLocationPurpose.STORAGE] as Icon,
  },
];

export const UnassignedBinContent: FC<UnassignedBinContentProps> = ({
  warningData,
  onDismiss,
}) => {
  const theme = useTheme();
  const [assignStockLocations, { isLoading: isLoadingAssignStockLocations }] =
    useAssignStockLocationsMutation();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const { control, handleSubmit, watch } = useTypedForm(unassignedBinSchema, {
    defaultValues: {
      purpose: StockLocationPurpose.PUTTING,
    },
  });

  const selectedPurpose = watch('purpose');

  const getTargetBinLocation = () => {
    if (!warningData.handlingRequest) {
      return {
        ...warningData.destination,
        purpose: selectedPurpose as StockLocationPurpose,
        org: getOrgId(warningData.stockLot.org) || '',
      };
    }

    const purpose =
      requestTypeToPurpose[warningData.handlingRequest.requestType];
    if (!purpose) {
      return warningData.destination;
    }

    const targetLocation = {
      ...warningData.destination,
      purpose,
    };

    if (
      KIT_REQUEST_PURPOSES.includes(purpose) &&
      warningData.handlingRequest.kitRequestIdViaLot
    ) {
      targetLocation.kitRequest =
        warningData.handlingRequest.kitRequestIdViaLot;
    }

    if (purpose === StockLocationPurpose.RECEIVING) {
      // Note: We don't have shipment info in the handling request, so this would be null
      // The BinLocationCard will handle this gracefully
    }

    const stockLotOrgId = getOrgId(warningData.stockLot.org);
    if (purpose === StockLocationPurpose.PUTTING && stockLotOrgId) {
      targetLocation.org = stockLotOrgId;
    }

    // Team assignment for putting purpose (if using teams)
    // Note: We don't have team info in the handling request, so this would be null
    // The BinLocationCard will handle this gracefully

    return targetLocation;
  };

  const getDescription = () => {
    if (!warningData.handlingRequest) {
      return 'This bin needs to be assigned a purpose. For stock lots without handling requests, you can assign it to either putting or storage.';
    }
    return "This bin needs to be assigned a purpose to match the stock lot's handling request.";
  };

  const getResolutionText = () => {
    if (!warningData.handlingRequest) {
      return "Select a purpose and click 'Reassign' to assign this bin, or return the stock lot to another matching bin";
    }
    return "Click 'Reassign' to assign this bin, or return the stock lot to another matching bin";
  };

  const handleReassign = useCallback(
    async (data: UnassignedBinFormData) => {
      try {
        const handlingRequest = warningData.handlingRequest;

        const purpose = handlingRequest
          ? requestTypeToPurpose[handlingRequest.requestType]
          : (data.purpose as StockLocationPurpose);

        if (!purpose) {
          dispatchErrorSnackbar(
            `Handling request type "${handlingRequest?.requestType}" does not require a specific bin purpose`
          );
          return;
        }

        const assignPayload = {
          purpose,
          ...(KIT_REQUEST_PURPOSES.includes(purpose) &&
            handlingRequest?.kitRequestIdViaLot && {
              kitRequest: handlingRequest.kitRequestIdViaLot,
            }),
          ...(purpose === StockLocationPurpose.PUTTING && {
            org: getOrgId(warningData.stockLot.org) || '',
          }),
        };

        const locationToAssign =
          warningData.destination.parent || warningData.destination.id;

        await assignStockLocations({
          id: locationToAssign,
          payload: assignPayload,
        }).unwrap();

        dispatchSuccessSnackbar('Bin assigned successfully');
        onDismiss();
      } catch (error) {
        handleErr(error, (errorMessage) => {
          dispatchErrorSnackbar(errorMessage);
        });
      }
    },
    [
      assignStockLocations,
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      onDismiss,
      warningData,
    ]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Required Action:
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {getDescription()}
      </Typography>
      {!warningData.handlingRequest && (
        <Box sx={{ mb: 3 }}>
          <HookFormClickableBox
            name="purpose"
            control={control}
            gridProps={{ xs: 12, md: 6 }}
            options={unassignedBinOptions}
          />
        </Box>
      )}
      <Stack spacing={3} alignItems="center">
        {warningData.handlingRequest && (
          <StockLotRequestCard warningData={warningData} />
        )}
        <Stack direction="row" spacing={2} alignItems="center">
          <BinLocationCard stockLocation={warningData.destination} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <IconArrowRight
              size={24}
              color={theme.palette.primary.main}
              style={{ marginBottom: '4px' }}
            />
            <Typography
              variant="caption"
              color="primary"
              sx={{ fontWeight: 500 }}
            >
              Assign Purpose
            </Typography>
          </Box>
          <BinLocationCard stockLocation={getTargetBinLocation()} />
        </Stack>
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, textAlign: 'center' }}
      >
        {getResolutionText()}
      </Typography>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
      >
        <SegmentLoadingButton
          color="primary"
          variant="contained"
          size="large"
          loading={isLoadingAssignStockLocations}
          onClick={handleSubmit(handleReassign)}
          sx={{ mt: 3, width: '100%' }}
        >
          Reassign
        </SegmentLoadingButton>
      </Stack>
    </Box>
  );
};
